/**
 * The public pages' side of first and last touch: the analytics-consent answer and the
 * first-party touch history, both kept in cookies on the tenant's shared public domain.
 * Every decision is the contract's (api-contracts public-attribution.ts); this module only
 * reads and writes the cookies and holds the arrival's touch in memory.
 *
 * Data: unbound. No request of its own. `attribution` rides the existing public registration
 * and booking requests (events-public-data.ts, appointment-data.ts); not yet observed on the wire.
 *
 * Never throws into a registration: an unreadable cookie is no history, and anything the server
 * cannot use it drops.
 */
import {
  PUBLIC_ANALYTICS_CONSENT_COOKIE,
  PUBLIC_ANALYTICS_CONSENT_MAX_AGE_SECONDS,
  PUBLIC_TOUCH_COOKIE,
  PUBLIC_TOUCH_COOKIE_MAX_AGE_SECONDS,
  decodePublicTouchCookie,
  encodePublicTouchConsentCookie,
  encodePublicTouchCookie,
  nextPublicTouchHistory,
  publicAttributionForRegistration,
  publicTouchConsentFromCookie,
  publicTouchCookieDomain,
  publicTouchForPageLoad,
} from "@v0y4ge/api-contracts";
import type { PublicAttributionInput, PublicTouch, PublicTouchConsent, PublicTouchHistory } from "@v0y4ge/api-contracts";

/** Set once a public host has recorded its first page load; until then nothing is sent. */
let active = false;
/** The visit's arrival, in memory only (never in storage). */
let arrival: { href: string; referrer: string; at: Date } | null = null;
let entryTouch: PublicTouch | null = null;
let lastHref: string | null = null;
let reopened = false;
const listeners = new Set<() => void>();

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    for (const part of document.cookie ? document.cookie.split("; ") : []) {
      const eq = part.indexOf("=");
      if ((eq < 0 ? part : part.slice(0, eq)) === name) return eq < 0 ? "" : part.slice(eq + 1);
    }
  } catch { /* unreadable cookie jar: no answer, no history */ }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  const domain = publicTouchCookieDomain(window.location.hostname);
  try {
    document.cookie = `${name}=${value}${domain ? `; Domain=${domain}` : ""}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax; Secure`;
  } catch { /* a refused write leaves the previous value; never blocks the page */ }
}

function deleteTouchCookie(): void {
  writeCookie(PUBLIC_TOUCH_COOKIE, "", 0);
}

function readHistory(): PublicTouchHistory | null {
  return decodePublicTouchCookie(readCookie(PUBLIC_TOUCH_COOKIE));
}

function keepHistory(history: PublicTouchHistory | null): void {
  if (!history) {
    if (readCookie(PUBLIC_TOUCH_COOKIE) !== null) deleteTouchCookie();
    return;
  }
  writeCookie(PUBLIC_TOUCH_COOKIE, encodePublicTouchCookie(history), PUBLIC_TOUCH_COOKIE_MAX_AGE_SECONDS);
}

function notify(): void {
  for (const fn of listeners) fn();
}

export function analyticsConsent(): PublicTouchConsent {
  return publicTouchConsentFromCookie(readCookie(PUBLIC_ANALYTICS_CONSENT_COOKIE));
}

/** Whether the banner should show: no answer on record, or reopened from "Cookie settings". */
export function consentBannerOpen(): boolean {
  const answer = readCookie(PUBLIC_ANALYTICS_CONSENT_COOKIE);
  return reopened || (answer !== "granted" && answer !== "denied");
}

export function subscribeConsent(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** "Cookie settings": show the banner again. */
export function reopenConsentBanner(): void { // ui-local
  reopened = true;
  notify();
}

/**
 * One public page load (the first, and every in-app navigation after it). The arrival's own
 * referrer is read once; a later navigation's referrer is the page before it, which is the
 * tenant's own page and so never a new touch on its own.
 */
export function recordPublicPageLoad(): void {
  if (typeof window === "undefined") return;
  const href = window.location.href;
  if (href === lastHref) return;
  const now = new Date();
  const referrer = lastHref ?? document.referrer;
  if (!arrival) {
    arrival = { href, referrer: document.referrer, at: now };
    entryTouch = publicTouchForPageLoad(href, document.referrer, now);
  }
  lastHref = href;
  active = true;
  keepHistory(nextPublicTouchHistory(readHistory(), href, referrer, analyticsConsent(), now));
}

/** Accept or Decline. Accepting seeds the history from the arrival, not the page it was accepted on. */
export function setAnalyticsConsent(accepted: boolean): void { // ui-local (first-party cookie only)
  writeCookie(PUBLIC_ANALYTICS_CONSENT_COOKIE, encodePublicTouchConsentCookie(accepted), PUBLIC_ANALYTICS_CONSENT_MAX_AGE_SECONDS);
  if (!accepted) deleteTouchCookie();
  else if (arrival) keepHistory(nextPublicTouchHistory(readHistory(), arrival.href, arrival.referrer, { analytics: true }, arrival.at));
  reopened = false;
  notify();
}

/** The `attribution` for a public registration or booking submit, or undefined to omit the field. */
export function publicAttributionForSubmit(): PublicAttributionInput | undefined {
  if (!active) return undefined;
  try {
    return publicAttributionForRegistration({ history: readHistory(), consent: analyticsConsent(), entryTouch }, new Date());
  } catch {
    return undefined;
  }
}
